// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { getCart, removeCartBundle, selectHasBundleByType } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Text } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { FullState } from 'src/types';

import {
  BUNDLE_TYPE_POPULARITY,
  selectBundle,
} from '../../../modules/selectors/bundle';
import {
  selectBundledResults,
  selectOne as selectSuggestion,
  selectProvidedList,
} from '../../../modules/selectors/results';

import type {
  Bundle,
  ExtendedSuggestion,
  Phase,
  PhasePrice,
} from '../../../types';

import { DEFAULT_PHASE } from '../../DomainList/constant';

import { addBundleToCart } from './bundle';

export const selectShowBulk = (state: FullState): boolean => {
  const fqdns = selectProvidedList(state, 'popularity');

  if (fqdns.length <= 1) {
    return false;
  }

  for (let i = 0; i < fqdns.length; i += 1) {
    // $FlowFixMe[incompatible-type]
    const result: ExtendedSuggestion = selectSuggestion(state, fqdns[i]);
    if (
      !result.addedToCart &&
      result.availability === 'available' &&
      result.prices
    ) {
      return true;
    }
  }

  return false;
};

const NULL_DOMAIN_BUNDLE = {
  price: 0,
  normalPrice: 0,
  formattedDiscountPercent: '',
  formattedDiscount: { discountPercent: 0, priceTenRatio: 10 },
  currency: 'EUR',
  bundle: undefined,
  addBundle: () => Promise.resolve(),
  removeBundle: () => Promise.resolve(),
  loading: false,
  allInTheCart: false,
};

function getFormattedDiscount({
  price,
  normalPrice,
}: {
  price: number,
  normalPrice: number,
}): { discountPercent: number, priceTenRatio: number } {
  const discountPercent = 100 - Math.round((price / normalPrice) * 100);
  return {
    discountPercent,
    // Chinese format for discounts - https://phabricator.corp.gandi.net/T54845
    priceTenRatio: (100 - parseFloat(discountPercent)) / 10,
  };
}

export function useDomainBundle(): {
  price: number,
  normalPrice: number,
  formattedDiscountPercent: string,
  formattedDiscount: ReturnType<typeof getFormattedDiscount>,
  currency: string,
  bundle: Bundle | void,
  addBundle: (evt: SyntheticMouseEvent<>) => mixed,
  removeBundle: (evt: SyntheticMouseEvent<>) => mixed,
  loading: boolean,
  allInTheCart: boolean,
} {
  const [loading, setLoading] = React.useState(false);
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  const taxesIncluded = useAppSelector(selectTaxesDisplaySetting);

  const bundle = useAppSelector(state =>
    selectBundle(state, BUNDLE_TYPE_POPULARITY),
  );

  const bundledSuggestions = useAppSelector(state =>
    selectBundledResults(state, BUNDLE_TYPE_POPULARITY),
  );

  const cartHasBundlePopularity = useAppSelector(state =>
    selectHasBundleByType(state, BUNDLE_TYPE_POPULARITY),
  );

  if (!bundle) return NULL_DOMAIN_BUNDLE;

  const { total } = bundle;

  if (!total) return NULL_DOMAIN_BUNDLE;

  const { price, normalPrice, currency } = total;

  if (!price || !normalPrice || !currency) return NULL_DOMAIN_BUNDLE;

  const addBundle = async (evt: SyntheticMouseEvent<>): mixed => {
    evt.preventDefault();

    // We have to set a const to help flow checking
    const { items: bundleItems } = bundle;
    if (!bundleItems) return undefined;

    setLoading(true);

    try {
      await dispatch(
        addBundleToCart({
          bundledSuggestions,
          bundleItems,
          bundleGrids: bundle.grids,
          bundleType: bundle.type,
          bundleUseOnce: bundle.use_once,
        }),
      );
    } catch (err) {
      notifyDanger(__('Unable to add the products in the cart.'));
      return Promise.reject();
    } finally {
      setLoading(false);
    }

    return undefined;
  };

  const removeBundle = async (evt: SyntheticMouseEvent<>): mixed => {
    evt.preventDefault();

    const bundleId = bundledSuggestions[0].cartBundleId;
    if (!bundleId) return;

    setLoading(true);

    try {
      await dispatch(removeCartBundle(bundleId));
    } catch (err) {
      notifyDanger(__('Unable to remove the products in the cart.'));
    } finally {
      setLoading(false);
      // $FlowFixMe[incompatible-call]
      dispatch(getCart()).catch({});
    }
  };

  const formattedDiscount = getFormattedDiscount({
    price: price.beforeTaxes,
    normalPrice: normalPrice.beforeTaxes,
  });
  const formattedDiscountPercent = `${formattedDiscount.discountPercent}%`;

  return {
    normalPrice: taxesIncluded
      ? normalPrice.afterTaxes
      : normalPrice.beforeTaxes,
    price: taxesIncluded ? price.afterTaxes : price.beforeTaxes,
    formattedDiscount,
    formattedDiscountPercent,
    currency,
    bundle,
    addBundle,
    removeBundle,
    loading,
    allInTheCart:
      cartHasBundlePopularity &&
      bundledSuggestions.every(suggestion => suggestion.cartBundleId),
  };
}

/* usePriceText give the right price to show
 *  depending if phase selection is neeeded
 */
export const usePriceText = ({
  boldPrice,
  currency,
  minDuration,
  needsPhaseSelection,
  price,
}: {
  boldPrice?: boolean,
  currency: string,
  minDuration: number,
  needsPhaseSelection: boolean,
  price: number,
}): React.MixedElement => {
  const { __, formatPrice } = useTranslator();

  const priceText = formatPrice(price * minDuration, currency);
  const formattedPrice = boldPrice ? (
    <Text weight="semibold">{priceText}</Text>
  ) : (
    <span>{priceText}</span>
  );

  return needsPhaseSelection ? (
    <span
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={__('From %(price)s', {
        price: formattedPrice,
        withHTML: true,
        escapeParameters: false,
      })}
    />
  ) : (
    formattedPrice
  );
};

const EMPTY_PHASE_ARRAY: Array<Phase> = [];

const NULL_DOMAIN_SUGGESTION = {
  currency: 'EUR',
  discountNormalPrice: 0,
  formattedDiscount: { discountPercent: 0, priceTenRatio: 10 },
  renewPrice: 0,
  priceDiff: 0,
  renewMinDuration: 0,
  minDuration: 0,
  needsPhaseSelection: false,
  normalPrice: 0,
  price: 0,
  phases: EMPTY_PHASE_ARRAY,
  suggestion: undefined,
  valid: false,
  hasDiscount: false,
};

type ReturnExtendSuggestion = {
  currency: string,
  discountNormalPrice?: number,
  formattedDiscount?: ReturnType<typeof getFormattedDiscount>,
  phasePrice?: PhasePrice,
  renewPrice: number,
  priceDiff: number,
  renewMinDuration: number,
  minDuration: number,
  needsPhaseSelection: boolean,
  normalPrice: number,
  price: number,
  phases: Array<Phase>,
  suggestion: void | ExtendedSuggestion,
  valid: boolean,
  hasDiscount: boolean,
};

export function useExtendedSuggestion({
  fqdn,
}: {
  fqdn: string,
}): ReturnExtendSuggestion {
  const suggestion: ExtendedSuggestion | void = useAppSelector(state =>
    selectSuggestion(state, fqdn),
  );

  const taxesIncluded = useAppSelector(selectTaxesDisplaySetting);

  if (!suggestion) return NULL_DOMAIN_SUGGESTION;

  const { currency, prices } = suggestion;

  const phasePrice = prices?.create?.[suggestion.phase || DEFAULT_PHASE];
  const priceShape = phasePrice?.price;

  if (!phasePrice || !priceShape || !currency)
    return { ...NULL_DOMAIN_SUGGESTION, suggestion };

  // the most common case
  const price = taxesIncluded ? priceShape.afterTaxes : priceShape.beforeTaxes;

  const { minDuration, discount } = phasePrice;

  if (!minDuration) return { ...NULL_DOMAIN_SUGGESTION, suggestion };

  // when there is a discount
  const discountNormalPrice = taxesIncluded
    ? discount?.normalPrice.afterTaxes
    : discount?.normalPrice.beforeTaxes;
  const discountPrice = taxesIncluded
    ? discount?.price.afterTaxes
    : discount?.price.beforeTaxes;

  const hasDiscount = !!discountPrice && !!discountNormalPrice;

  const formattedDiscount =
    discount && !!discountNormalPrice && !!discountPrice
      ? getFormattedDiscount({
          normalPrice: discountNormalPrice,
          price: discountPrice,
        })
      : undefined;

  return {
    currency,
    discountNormalPrice,
    formattedDiscount,
    phasePrice,
    priceDiff: Number(prices?.renew?.priceDiff),
    renewPrice:
      (prices?.renew?.price &&
        (taxesIncluded
          ? prices.renew.price.afterTaxes
          : prices.renew.price.beforeTaxes)) ||
      0,
    renewMinDuration: prices?.renew?.minDuration || 0,
    minDuration: hasDiscount && discount ? discount.minDuration : minDuration,
    needsPhaseSelection: !!(
      prices?.create && [...prices.create.phases].length > 1
    ),
    normalPrice: discount ? discountNormalPrice || 0 : 0,
    price: discount ? discountPrice || 0 : price,
    phases: prices?.create?.phases || EMPTY_PHASE_ARRAY,
    suggestion,
    valid:
      !suggestion.error &&
      suggestion.availability === 'available' &&
      !!suggestion.prices &&
      !!suggestion.prices.create,
    hasDiscount: !!discountPrice && !!discountNormalPrice,
  };
}

export const useDurationText = ({
  minDuration,
  timeLimited,
}: {
  minDuration: number,
  timeLimited: boolean,
}): string => {
  const { __ } = useTranslator();
  if (timeLimited) {
    return __('%(count)s year at', { count: minDuration });
  }

  return minDuration === 1
    ? __('/year')
    : // TODO: clarify if we should still use this in some cases:
      __('/%(count)s years', { count: minDuration });
};
